<script setup lang="ts">
import { computed } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IProps {
  title: string;
  subtitle?: string;
  submitText?: string;
  cancelText?: string;
  deleteMode?: boolean;
  isLoading?: boolean;
  callback: () => void | Promise<void>;
}

const props = defineProps<IProps>();

const visible = defineModel<boolean>();

const { t, k, rawT } = useTypedIntl();

const submitText = computed(() => {
  if (props.submitText) return props.submitText;

  return props.deleteMode ? t(k.global.misc.delete) : t(k.global.misc.submit);
});
</script>

<template>
  <BaseDialog
    v-model:visible="visible"
    :closable="!props.isLoading"
    :close-on-escape="!props.isLoading"
    class="w-[28rem]"
    modal
    size="small"
  >
    <template #header>
      <div class="flex flex-col gap-4">
        <div
          v-if="props.deleteMode"
          class="h-[48px] w-[48px] bg-red-100/50 text-red-600 flex items-center justify-center rounded-full"
        >
          <i
            :data-test-id="DATA_TEST_IDS.components.confirmationDialog.icon"
            class="pi pi-exclamation-triangle"
          />
        </div>
        <h4
          class="font-semibold"
          :data-test-id="DATA_TEST_IDS.components.confirmationDialog.title"
        >
          {{ props.title }}
        </h4>
      </div>
    </template>

    <span
      v-if="props.subtitle"
      :data-test-id="DATA_TEST_IDS.components.confirmationDialog.subtitle"
      class="text-text-secondary"
    >
      {{ props.subtitle }}
    </span>

    <template #footer>
      <div class="grid grid-flow-col grid-cols-5 gap-2 w-full">
        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.confirmationDialog.cancelButton"
          class="col-span-2"
          :disabled="props.isLoading"
          :label="props.cancelText ?? t(k.global.misc.cancel)"
          severity="secondary"
          @click="visible = false"
          @keydown.enter="visible = false"
        />
        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.confirmationDialog.submitButton"
          class="col-span-3"
          :disabled="props.isLoading"
          :label="rawT(`${submitText}`)"
          :loading="props.isLoading"
          :severity="props.deleteMode ? 'destructive' : undefined"
          @click="props.callback()"
        />
      </div>
    </template>
  </BaseDialog>
</template>
